import {
  TableRow,
  TableCell,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { fmtDateParts, initials } from "./LeadUtils";
import {
  getColorFromPalette,
  getSourceStyles,
} from "../../../config/leadStatus";
import { TelecallerDropdown } from "./TelecallerDropdown";

const LeadRow = ({
  l,
  onViewDetails,
  telecallers = [],
  treatments = [],
  statusOptions,
  onStatusChange,
  onPriorityChange,
  onTelecallerChange,
  onTreatmentChange,
  onContactInfoClick,
}) => {
  const theme = useTheme();
  const d = fmtDateParts(l.createdAt);
  const sourceStyle = getSourceStyles(l.source);
  const avatarStyle = getColorFromPalette(l.name);

  const TREATMENT_OPTIONS = (treatments || []).map((t) => ({
    key: t,
    label: t,
  }));

  const norm = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const getTreatmentStyles = (t) => {
    const s = getColorFromPalette(String(t || "—"));
    return {
      bgcolor: s.bgcolor,
      color: s.color,
      label: String(t || "—"),
    };
  };

  const firstLetter = (label = "") => {
    const t = String(label).trim();
    return (t[0] || "?").toUpperCase();
  };

  const TreatmentAvatars = ({ services, options, onRemove }) => {
    const serviceSet = new Set(
      (Array.isArray(services) ? services : []).map(norm),
    );

    const selected = options.filter((opt) => serviceSet.has(norm(opt.key)));

    if (!selected.length) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.6, fontStyle: "italic" }}>
          No service selected
        </Typography>
      );
    }

    const visible = selected.slice(0, 2);
    const extra = selected.length - visible.length;

    return (
      <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
        {visible.map((opt, idx) => (
          <Tooltip key={opt.key} title={opt.label} arrow>
            <Avatar
              onClick={() => onRemove?.(opt.key)}
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                fontWeight: 800,
                cursor: onRemove ? "pointer" : "default",
                bgcolor: getTreatmentStyles(opt.label).color,

                ml: idx === 0 ? 0 : -1.1,
                border: "2px solid #fff",
                boxSizing: "content-box",
              }}
            >
              {firstLetter(opt.label)}
            </Avatar>
          </Tooltip>
        ))}

        {extra > 0 && (
          <Tooltip
            title={selected
              .slice(2)
              .map((x) => x.label)
              .join(", ")}
            arrow
          >
            <Chip
              label={`+${extra}`}
              size="small"
              sx={{
                height: 28,
                ml: -1.1,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 11,
                px: 0.5,
                bgcolor: "#EEF2FF",
                color: "#4F46E5",
                border: "2px solid #fff",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <TableRow
      hover
      sx={{ "& td": { borderBottom: "1px solid #F1F5F9", py: 1.25, px: 1 } }}
    >
      {/* DATE */}
      <TableCell>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1E293B", whiteSpace: "nowrap" }}>
            {d ? `${d.top} ${d.mid}` : "-"}
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 500, color: "#94A3B8" }}>
            {d?.bot || ""}
          </Typography>
        </Stack>
      </TableCell>

      {/* SOURCE */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: sourceStyle.bgcolor,
              color: sourceStyle.color,
            }}
          >
            {sourceStyle.icon}
          </Avatar>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#475569",
            }}
            textTransform={"capitalize"}
          >
            {l.source || "-"}
          </Typography>
        </Stack>
      </TableCell>

      {/* NAME */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: avatarStyle.bgcolor,
              color: avatarStyle.color,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {initials(l.name)}
          </Avatar>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
            {l.name}
          </Typography>
        </Stack>
      </TableCell>

      {/* CONTACT - Click to open popup */}
      <TableCell>
        <Stack
          direction="row"
          spacing={0.5}
          onClick={() =>
            onContactInfoClick?.({
              phone: l.phone,
              email: l.email,
              address: l.location || l._raw?.address || "",
              clientName: l.name,
            })
          }
          sx={{ cursor: "pointer" }}
        >
          <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#E2E8F0" },
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 14, color: "#64748B" }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#E2E8F0" },
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 14, color: "#64748B" }} />
          </IconButton>
        </Stack>
      </TableCell>

      {/* TREATMENT TYPE */}
      <TableCell>
        <TreatmentAvatars
          services={l.services}
          options={TREATMENT_OPTIONS}
          onRemove={(key) => {
            const curr = Array.isArray(l.services) ? l.services : [];
            const next = curr.filter((x) => x !== key);
            onTreatmentChange?.(l._id, next);
          }}
        />
      </TableCell>

      {/* TELECALLER */}

      <TableCell>
        <TelecallerDropdown
          value={l.telecallerId || ""}
          telecallers={telecallers}
          onSelect={(userId) => onTelecallerChange(l._id, userId)}
        />
      </TableCell>

      {/* ACTION */}
      <TableCell align="right" sx={{ pr: 2 }}>
        <Button
          onClick={() => onViewDetails(l)}
          variant="outlined"
          size="small"
          sx={{
            minWidth: "auto",
            height: 28,
            px: 1.5,
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 11,
            borderColor: "#E2E8F0",
            color: theme.palette.primary.main,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderColor: alpha(theme.palette.primary.main, 0.3),
            },
          }}
        >
          View Details
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default LeadRow;
